import { hashCode } from "./misc";

// Physical inventory count / reconciliation helpers.
//
// A "count" (session) freezes an EXPECTED snapshot of every item we believe is
// at each in-scope location when it is created. As the warehouse is walked, a
// counter records the quantity actually found per line (`countedQty`). Lines
// left uncounted when the session is completed are treated as MISSING; items
// found that were not expected are added as EXTRA lines. The reconciliation
// report is derived entirely from the frozen lines, so it always answers
// "what we believed was here at time T, and what we actually found".
//
// This module is deliberately free of Vue/Vuex/Firestore so the snapshot and
// reconciliation math can be unit-tested in isolation.

export const COUNT_LINE_RESULTS = {
  match: { label: "Match", icon: "done", color: "positive" },
  partial: { label: "Quantity off", icon: "warning", color: "warning" },
  missing: { label: "Missing", icon: "missing", color: "negative" },
  extra: { label: "Extra", icon: "add", color: "info" },
  pending: { label: "Not counted", icon: "pending", color: "grey" },
};

export const COUNT_STATUSES = {
  active: {
    label: "In progress",
    icon: "inventory_count_active",
    color: "warning",
  },
  complete: {
    label: "Complete",
    icon: "inventory_count_complete",
    color: "positive",
  },
};

/**
 * Stable id for a line (its document id under inventoryCounts/{id}/lines),
 * derived from the item's Firestore path and the location key. A document id
 * cannot contain `/`, so we hash both halves into a safe token.
 */
export function makeCountLineId(itemPath = "", locationKey = "") {
  // hashCode can be negative; `-` is fine in a Firestore document id but we
  // normalize it to keep the token strictly alphanumeric + underscore.
  const token = (s) => String(hashCode(String(s))).replace("-", "n");
  return `l${token(itemPath)}_${token(locationKey)}`;
}

/**
 * A line is "counted" once `countedQty` is a number (0 is a valid count — the
 * shelf was checked and found empty). `null`/`undefined` means untouched.
 */
export function isCountLineCounted(line = {}) {
  return typeof line.countedQty === "number";
}

/**
 * Classify a single line.
 * @param {object} line
 * @param {boolean} final  Treat uncounted lines as MISSING (report/complete)
 *   rather than PENDING (mid-count).
 */
export function countLineResult(line = {}, final = false) {
  if (line.extra) {
    return "extra";
  }
  if (!isCountLineCounted(line)) {
    return final ? "missing" : "pending";
  }
  return line.countedQty === line.expectedQty ? "match" : "partial";
}

/**
 * The quantity variance (found − expected) for a line. Uncounted lines count
 * as 0 found when `final`, otherwise contribute nothing.
 */
export function countLineQtyVariance(line = {}, final = false) {
  const expected = Number(line.expectedQty) || 0;
  const counted = isCountLineCounted(line)
    ? line.countedQty
    : final
    ? 0
    : expected;
  return counted - expected;
}

function toLineArray(lines) {
  if (!lines) return [];
  return Array.isArray(lines) ? lines : Object.values(lines);
}

/**
 * Roll a set of lines up into a reconciliation summary: a tally per result
 * type, progress (how many of the expected lines have been counted), and the
 * dollar variance of the discrepancies.
 */
export function summarizeCount(lines, final = false) {
  const list = toLineArray(lines);
  const counts = {
    match: 0,
    partial: 0,
    missing: 0,
    extra: 0,
    pending: 0,
  };
  let expectedValue = 0;
  let countedValue = 0;
  let expectedLines = 0;
  let countedLines = 0;

  list.forEach((line) => {
    const result = countLineResult(line, final);
    counts[result] += 1;

    const unitCost = Number(line.cost) || 0;
    if (!line.extra) {
      expectedLines += 1;
      expectedValue += unitCost * (Number(line.expectedQty) || 0);
      if (isCountLineCounted(line)) {
        countedLines += 1;
      }
    }
    const countedQty = isCountLineCounted(line)
      ? line.countedQty
      : final
      ? 0
      : Number(line.expectedQty) || 0;
    countedValue += unitCost * countedQty;
  });

  return {
    counts,
    total: list.length,
    expectedLines,
    countedLines,
    progress: expectedLines ? countedLines / expectedLines : 0,
    discrepancies: counts.partial + counts.missing + counts.extra,
    expectedValue,
    countedValue,
    costVariance: countedValue - expectedValue,
  };
}

/** A count's lines in display order: by location, then PO. */
export function sortCountLines(lines = []) {
  return [...lines].sort((a, b) => {
    const loc = String(a.locationLabel || "").localeCompare(
      String(b.locationLabel || "")
    );
    return loc || String(a.po || "").localeCompare(String(b.po || ""));
  });
}

/**
 * Decide whether an `ItemLocation` is in scope for a count.
 * @param {ItemLocation} location
 * @param {object} scope { warehouses: string[], includeVehicles: boolean }
 *   Only the listed warehouses are counted; an empty list counts none.
 */
export function isLocationInCountScope(location, scope = {}) {
  if (!location) return false;
  const warehouses = scope.warehouses || [];
  if (location.isWarehouseZone) {
    return warehouses.includes(location.warehouse);
  }
  if (location.isVehicle) {
    return Boolean(scope.includeVehicles);
  }
  // temp / final / installed / delivered are not "on hand" and never counted.
  return false;
}

/**
 * Build the frozen expected-line map for a new count from the on-hand items
 * currently in the store.
 *
 * @param {Array} items  InventoryItem / SaleItem instances (each exposes
 *   `itemLocation`, `data.cost`, description/series metadata and an id path).
 * @param {object} scope { warehouses, includeVehicles }
 * @param {(item, location) => object} describe  Maps a source item + location
 *   to the denormalized line fields (po, brand, series, description, itemPath,
 *   itemType, cost). Kept as a callback so this module stays class-agnostic.
 * @returns {object} lines keyed by lineId (each becomes a document,
 *   inventoryCounts/{id}/lines/{lineId})
 */
export function buildCountLines(items = [], scope = {}, describe) {
  const lines = {};
  items.forEach((item) => {
    const locations = item.itemLocation || [];
    locations.forEach((location) => {
      if (!isLocationInCountScope(location, scope)) {
        return;
      }
      const quantity = Number(location.quantity) || 0;
      if (quantity <= 0) {
        return;
      }
      const described = describe(item, location) || {};
      const id = makeCountLineId(described.itemPath, location.key);
      if (lines[id]) {
        // Same item already recorded at an identically-keyed location: merge
        // the expected quantities rather than dropping one.
        lines[id].expectedQty += quantity;
        return;
      }
      lines[id] = {
        ...described,
        locationKey: location.key,
        locationLabel: location.displayNameWithWarehouse,
        warehouse: location.warehouse || "",
        expectedQty: quantity,
        countedQty: null,
        extra: false,
        countedBy: null,
        countedAt: null,
        note: "",
      };
    });
  });
  return lines;
}
